import hiClient from "../axios/hiClient";
import { useFetch } from "../hooks/useFetch";
import { DeviceDetailCard } from "./DeviceDetailCard";
import { PieChartCard } from "./PieChartCard";
import primeClient from "../axios/primeClient";
import { timeAgo } from "../utils/timeAgo";
import { useTranslation } from "react-i18next";
import { parseDate } from "../utils/parseDate";
import { useEffect } from "react";

export const Analytics = () => {
  const loginType = localStorage.getItem("login_type");
  const isMobile = window.innerWidth <= 570;
  const { t } = useTranslation();

  if (!loginType) return null;

  return (
    <div
      style={{
        padding: "2rem 1rem 0 1rem",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <div
        style={{
          flexGrow: "1",
          display: "grid",
          gridTemplateRows: "4fr 5.5fr",
          gap: "1rem",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontSize: "1.25rem",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            {t("prime.deviceCondition.header")}
          </div>
          <PieChartCard />
        </div>
        {loginType === "prime" ? (
          isMobile ? (
            <div
              style={{
                height: isMobile ? "100%" : "0",
              }}
            >
              <PrimeDeviceCardGroup />
            </div>
          ) : (
            <PrimeDeviceCardGroup />
          )
        ) : (
          <div
            style={{
              height: isMobile ? "100%" : "auto",
            }}
          >
            <HiDeviceCardGroup />
          </div>
        )}
      </div>
    </div>
  );
};

const PrimeDeviceCardGroup = () => {
  const { t, i18n } = useTranslation();
  const { data: upData, isLoading: loadingUpData } = useFetch({
    url: "/v1/apps/list?status=up",
    client: primeClient,
    initialData: { device: [] },
  });
  const { data: downData, isLoading: loadingDownData } = useFetch({
    url: "/v1/apps/list?status=down",
    client: primeClient,
    initialData: { device: [] },
  });
  const { data: attentionData, isLoading: loadingAttentionData } = useFetch({
    url: "/v1/apps/list?status=attention",
    client: primeClient,
    initialData: { device: [] },
  });

  const dataMapper = (el, status) => ({
    id:
      el.type === "network"
        ? el.network_id
        : el.type === "server"
        ? el.server_id
        : el.web_id,
    name: el.name,
    identifier: el.type === "webapi" ? el.url : el.ip_address,
    lastUpdated: timeAgo(el.last_update, t),
    type: el.type,
    description: [
      ...el.root_cause.component.critical.map((comp) => {
        return i18n.language === "id"
          ? `Kapasitas ${comp.replaceAll("_", " ")} perangkat kritis (>80%)`
          : `Device ${comp.replaceAll("_", " ")} capacity is critical (>80%)`;
      }),
      ...el.root_cause.component.warning.map((comp) => {
        return i18n.language === "id"
          ? `Kapasitas ${comp.replaceAll("_", " ")} perangkat warning (>60%)`
          : `Device ${comp.replaceAll("_", " ")} capacity is warning (>60%)`;
      }),
      ...el.root_cause.network.poor.map((comp) => {
        return i18n.language === "id"
          ? `Kualitas ${comp.replaceAll("_", " ")} perangkat buruk`
          : `Device ${comp.replaceAll("_", " ")} is poor`;
      }),
      ...(status === "down" ? [t("prime.deviceList.deviceOff")] : []),
    ],
    last_update: el.last_update,
  });

  const sortByLastUpdated = (data) =>
    [...data].sort(
      (a, b) => parseDate(b.last_update) - parseDate(a.last_update)
    );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          window.innerWidth <= 600
            ? "repeat(auto-fill, minmax(220px, 1fr))"
            : "repeat(3, 1fr)",
        justifyContent: "space-between",
        gap: "0.5rem",
        height: window.innerWidth <= 600 ? "100%" : "100%",
        overflow: "hidden",
        paddingBottom: "2px",
      }}
    >
      <DeviceDetailCard
        headerText={"Device Clear"}
        loginType={"prime"}
        data={sortByLastUpdated(upData.device.map(dataMapper))}
        loading={loadingUpData}
      />
      <DeviceDetailCard
        headerText={"Device Down"}
        loginType={"prime"}
        data={sortByLastUpdated(
          downData.device.map((el) => dataMapper(el, "down"))
        )}
        loading={loadingDownData}
      />
      <DeviceDetailCard
        headerText={"Device Attention"}
        loginType={"prime"}
        data={sortByLastUpdated(
          attentionData.device.map((el) => dataMapper(el, "attention"))
        )}
        loading={loadingAttentionData}
      />
      <div
        style={{
          height: window.innerWidth <= 600 ? "40px" : 0,
        }}
      />
    </div>
  );
};

const HiDeviceCardGroup = () => {
  const { data } = useFetch({
    url: "/mobile/internet/list?status_internet=ALL",
    client: hiClient,
    initialData: {
      online_internet: [],
      dying_internet: [],
      offline_internet: [],
      los_internet: [],
      unknown_internet: [],
      sync_internet: [],
      suspend: [],
    },
  });
  const isMobile = window.innerWidth <= 570;

  const { t } = useTranslation();

  const dataMapper = (description) => (el) => ({
    id: el.id,
    name: el.Name,
    identifier: el.masked_nd,
    lastUpdated: timeAgo(el.updated_at, t),
    description: [description],
    last_update: el.updated_at,
  });

  const sortByLastUpdated = (data) =>
    [...data].sort(
      (a, b) => parseDate(b.last_update) - parseDate(a.last_update)
    );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          window.innerWidth <= 600
            ? "repeat(auto-fill, minmax(200px, 1fr))"
            : "repeat(2, 1fr)",
        justifyContent: "space-between",
        gap: "1rem",
        height: "100%",
      }}
    >
      <DeviceDetailCard
        headerText={"Internet Online"}
        loginType={"hi"}
        data={[
          ...sortByLastUpdated(
            data.online_internet.map(dataMapper("Internet Online"))
          ),
          ...sortByLastUpdated(
            data.dying_internet.map(dataMapper("Internet Dying Gasp"))
          ),
        ]}
      />
      <DeviceDetailCard
        headerText={"Internet Offline"}
        loginType={"hi"}
        data={[
          ...sortByLastUpdated(
            data.offline_internet.map(dataMapper("Internet Offline"))
          ),
          ...sortByLastUpdated(
            data.los_internet.map(dataMapper("Internet LOS"))
          ),
        ]}
      />
      <DeviceDetailCard
        headerText={"Internet Tech Issue"}
        loginType={"hi"}
        data={[
          ...sortByLastUpdated(
            data.unknown_internet.map(dataMapper("Internet Unknown"))
          ),
          ...sortByLastUpdated(
            data.sync_internet.map(dataMapper("Internet Sync"))
          ),
        ]}
      />
      <DeviceDetailCard
        headerText={"Internet Suspend"}
        loginType={"hi"}
        data={sortByLastUpdated(
          data.suspend.map(dataMapper("Internet Suspend"))
        )}
      />
      {isMobile && (
        <div
          style={{
            height: "50px",
          }}
        />
      )}
    </div>
  );
};
